/**
 * Sample-clip inputProps for the onboarding design studio (spec 2026-07-18
 * §4). A new channel has no rendered clip, so the wizard's Design step
 * mounts the REAL ClipComposition over a committed neutral gradient loop
 * (web/public/studio-sample.mp4) with representative sample cues in the
 * channel's language — every control behaves exactly as it will on real
 * clips because it IS the real composition.
 */
import type { ClipProps, FontFaceSpec, SerializableCue } from "../../../src/remotion/types.ts";

export const SAMPLE_DURATION_SEC = 6;

/**
 * Representative cues: a one-line with an accent span, a two-line punch, and a
 * long line that exercises auto-wrap. Cycled across the 6 s loop.
 *
 * The long one is deliberately kept at or under a REAL cue's budget
 * (`cueShape.maxChars`, 62 for the generic path). A longer sample was not a
 * harder test, it was a lie: at `maxLines: 2` the wrap folds the residue into
 * the last row, so the preview showed a caption running off the frame that no
 * pipeline would ever produce.
 */
const SAMPLE_CUES: Record<"rtl" | "ltr", SerializableCue[]> = {
  rtl: [
    { startMs: 0, endMs: 2000, text: "هذه <em>معاينة</em> التصميم" },
    { startMs: 2000, endMs: 4000, text: "السطر الأول\nوهنا الجملة الذهبية" },
    { startMs: 4000, endMs: 6000, text: "جملة أطول قليلًا لترى كيف يلتف النص" },
  ],
  ltr: [
    { startMs: 0, endMs: 2000, text: "This is your <em>design</em> preview" },
    { startMs: 2000, endMs: 4000, text: "First line on top\nthe punch line" },
    { startMs: 4000, endMs: 6000, text: "A slightly longer sentence to see how your captions wrap" },
  ],
};

/**
 * The longest of the three sample cues — the sentence the caption-size step
 * measures to answer "how many lines does a long sentence take at this size?".
 *
 * Longest by character count rather than a hard-coded index: the three cues
 * exist to exercise one-line / two-line / wrapping, and the wrapping one is by
 * definition the longest. Picking it by measurement means a reworded sample
 * can never leave the fit line quoting the short cue.
 */
export function sampleLongestCue(direction: "rtl" | "ltr"): string {
  return SAMPLE_CUES[direction].reduce((longest, c) => (c.text.length > longest.length ? c.text : longest), "");
}

export interface OnboardingSampleArgs {
  direction: "rtl" | "ltr";
  glyphRatio?: number;
  /** Brand values the wizard has collected so far. */
  captionColor: string;
  captionAccent: string;
  /** Speaker-card name colour when the brand splits it from the caption
   *  accent. Omitted → the accent, which is the founding card's gold. */
  speakerNameColor?: string;
  fonts: { caption: string; punch: string; speaker: string; speakerName?: string };
  /** Faces the preview can load: /api/fonts/... URLs for known families +
   *  a data: face for a not-yet-persisted wizard upload. */
  fontFaces: FontFaceSpec[];
  /** Wizard logo as an object/data URL — absent hides the logo layer. */
  logoSrc?: string;
}

/** A stand-in guest so the wizard's Design step shows a real speaker card —
 *  drawn LIVE from these strings (`live`), because a brand-new channel has no
 *  baked PNG on disk. Times it to the founding card feel: in at 0.5 s, gone
 *  before the 6 s loop repeats. */
const SAMPLE_SPEAKER: Record<"rtl" | "ltr", { name: string; description: string }> = {
  rtl: { name: "أليكس ريفيرا", description: "مدرّب مالي" },
  ltr: { name: "Alex Rivera", description: "Financial coach" },
};

export function buildSampleInputProps(a: OnboardingSampleArgs): ClipProps {
  return {
    videoSrc: "/studio-sample.mp4",
    durationSec: SAMPLE_DURATION_SEC,
    cues: SAMPLE_CUES[a.direction],
    speakers: [
      {
        slug: "sample",
        // Never fetched: the composition takes the `live` branch, which draws
        // the still from text instead of loading a PNG.
        pngSrc: "",
        startSec: 0.5,
        durationSec: 4,
        pngWidthPx: 2160,
        live: {
          ...SAMPLE_SPEAKER[a.direction],
          direction: a.direction,
          nameColor: a.speakerNameColor ?? a.captionAccent,
          descColor: a.captionColor,
          font: a.fonts.speaker,
          ...(a.fonts.speakerName ? { nameFont: a.fonts.speakerName } : {}),
          fontFaces: a.fontFaces,
        },
      },
    ],
    ...(a.logoSrc ? { logoSrc: a.logoSrc } : {}),
    brand: {
      captionColor: a.captionColor,
      captionAccent: a.captionAccent,
      captionFont: a.fonts.caption,
      punchFont: a.fonts.punch,
      speakerFont: a.fonts.speaker,
      direction: a.direction,
      ...(a.glyphRatio !== undefined ? { glyphRatio: a.glyphRatio } : {}),
    },
    fontFaces: a.fontFaces,
  };
}
